/*
 * NOBL cart engine (Prestige / CA).
 *
 * One persistent script (loaded once by sections/cart-drawer.liquid) that replaces the
 * per-render gift script and coordinates every cart write on the page:
 *
 *  1. Cart writes (/cart/add|change|update|clear) are serialised, so two writes can never
 *     overlap (Shopify drops one of two overlapping writes -> lost lines / wrong gifts).
 *  2. Free gifts are computed from the real cart JSON with the SAME rules the old Liquid
 *     snippet used (config comes from #nobl-gift-config), and written in ONE update.js.
 *  3. Quantity changes are queued/debounced and sent as one request (with the predicted
 *     gift changes), the drawer is repainted once, and never with stale quantities.
 *  4. The drawer is repainted with a "lite" render (no upsell list) unless the upsell list
 *     could have changed.
 *  5. Checkout buttons wait until quantities and gifts are settled.
 */
(function () {
  'use strict';
  if (window.NOBLCart) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var origFetch = window.fetch.bind(window);
  var JSON_HEADERS = { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
  var LITE_MARK = '__nobl_lite__';
  var FULL_MARK = '__nobl_full__';
  var NOUPS_MARK = '__nobl_noups__';
  var UPS_MARK = '__nobl_ups__';

  var CFG = null;
  var lastCart = null; // { cart, seq, at }
  var knownCart = null; // { cart, seq } last cart we know the content of (survives write invalidation)
  var addStamps = []; // adds already handled from the add response (consumed by the matching variant:add event)
  var lastFirst = null;
  var lastUpsellSig = null;
  var writeSeq = 0;
  var pendingWrites = 0;
  var chain = Promise.resolve();
  var idleWaiters = [];

  function S(v) { return v == null ? '' : String(v); }
  function readConfig() {
    var el = document.getElementById('nobl-gift-config');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  function cfg() { var c = readConfig(); if (c) CFG = c; return CFG; }

  // ---------------------------------------------------------------- write serialisation
  function urlOf(input) { return typeof input === 'string' ? input : (input && input.url) || ''; }
  function isCartWrite(url, init) {
    var m = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.exec(url);
    if (!m) return false;
    var method = ((init && init.method) || 'POST').toUpperCase();
    return method === 'POST';
  }
  function wakeIdle() {
    if (pendingWrites === 0) { var w = idleWaiters; idleWaiters = []; w.forEach(function (f) { f(); }); }
  }
  function idleWrites() {
    if (pendingWrites === 0) return Promise.resolve();
    return new Promise(function (resolve) { idleWaiters.push(resolve); });
  }
  window.fetch = function (input, init) {
    var url = urlOf(input);
    if (!isCartWrite(url, init)) return origFetch(input, init);
    pendingWrites++;
    var seq = ++writeSeq;
    lastCart = null; // any cached cart is stale as soon as a write is queued
    var run = chain.then(function () {
      function done() { pendingWrites--; wakeIdle(); }
      return origFetch(input, init).then(function (res) {
        if (res.ok && /\/cart\/(change|update)/.test(url)) {
          res.clone().json().then(function (j) {
            if (j && Array.isArray(j.items) && seq === writeSeq) { lastCart = { cart: j, seq: seq, at: Date.now() }; knownCart = { cart: j, seq: seq }; }
          }).catch(function () {});
        }
        done();
        if (res.ok && /\/cart\/add/.test(url)) {
          if (isThemeProductAdd(url, init)) pendingStart();
          res.clone().json().then(function (j) { handleAdd(j, seq); }).catch(function () {});
        }
        return res;
      }, function (err) { done(); throw err; });
    });
    chain = run.then(function () {}, function () {});
    return run;
  };


  // ---------------------------------------------------------------- opening the drawer after "Add to cart"
  // The drawer opens as soon as the first server render of the new cart (header, reserve timer, shipping bar, product,
  // footer) has been painted, i.e. before the free gifts are written and before the (heavy) upsell list is loaded.
  // Until the gifts are in, the opened drawer is not clickable.
  var pendingActive = false, pendingTimer = null, pendingLoading = false;
  function pendingLoadingEnd() {
    if (!pendingLoading) return;
    pendingLoading = false;
    loading(false);
  }
  function pendingEnd() {
    if (!pendingActive) return;
    pendingActive = false;
    clearTimeout(pendingTimer); pendingTimer = null;
    pendingLoadingEnd();
    var d = drawerEl();
    if (d) d.classList.remove('nobl-cart-pending');
  }
  function pendingReveal() {
    if (!pendingActive) return;
    pendingLoadingEnd();
    var d = drawerEl();
    if (d && !d.open && typeof d.show === 'function') d.show();
  }
  function pendingStart() {
    try {
      var d = drawerEl();
      if (!d || d.open || pendingActive || typeof d.show !== 'function') return;
      pendingActive = true;
      pendingLoading = true;
      loading(true);                                   // the theme's top loading bar keeps running until the drawer opens
      d.classList.add('nobl-cart-pending');
      clearTimeout(pendingTimer);
      pendingTimer = setTimeout(function () {          // never leave the customer waiting or the drawer locked
        pendingReveal();
        pendingTimer = setTimeout(pendingEnd, 8000);
      }, 6000);
    } catch (e) { pendingEnd(); }
  }
  function isThemeProductAdd(url, init) {
    if (!/\/cart\/add(\.js)?(\?|$)/.test(url)) return false;
    var body = init && init.body;
    if (!(window.FormData && body instanceof window.FormData) || !body.get('sections_url')) return false;
    var t = window.themeVariables && window.themeVariables.settings;
    return !!t && t.cartType === 'drawer' && t.pageType !== 'cart';
  }

  function fetchCartFresh() {
    return origFetch(ROOT + 'cart.js?_=' + Date.now(), { cache: 'no-store', credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }
  function getCart() {
    if (lastCart && pendingWrites === 0 && lastCart.seq === writeSeq && Date.now() - lastCart.at < 600000) {
      return Promise.resolve(lastCart.cart);
    }
    return idleWrites().then(fetchCartFresh).then(function (c) {
      if (pendingWrites === 0) { lastCart = { cart: c, seq: writeSeq, at: Date.now() }; knownCart = { cart: c, seq: writeSeq }; }
      return c;
    });
  }
  function setKnownCart(c) {
    if (c && Array.isArray(c.items) && pendingWrites === 0) { lastCart = { cart: c, seq: writeSeq, at: Date.now() }; knownCart = { cart: c, seq: writeSeq }; }
  }

  // Fast path for adds: the add response already tells us what was added, so predict the new cart from the last
  // known one and start the drawer repaint + gift sync right away instead of waiting for the theme to re-read /cart.js.
  function predictAdd(base, j) {
    var added = Array.isArray(j.items) ? j.items : (j && j.key ? [j] : []);
    if (!added.length) return null;
    var items = base.items.map(function (it) { return it; });
    var count = base.item_count || 0, total = base.total_price || 0;
    added.forEach(function (a) {
      var qty = a.quantity || 0, idx = -1;
      total += (a.final_line_price != null ? a.final_line_price : (a.line_price || 0));
      for (var i = 0; i < items.length; i++) { if (items[i].key === a.key) { idx = i; break; } }
      if (idx > -1) {
        var old = items[idx], n = Object.assign({}, old), oq = old.quantity || 1;
        n.quantity = oq + qty;
        n.line_price = Math.round((old.line_price || 0) / oq * n.quantity);
        n.final_line_price = Math.round((old.final_line_price || 0) / oq * n.quantity);
        items[idx] = n;
      } else items.push(Object.assign({}, a));
      count += qty;
    });
    return { items: items, item_count: count, total_price: total };
  }
  function handleAdd(j, seq) {
    try {
      if (!j || !window.NOBLCart || !cfg() || !drawerEl()) return;
      if (!knownCart || knownCart.seq !== seq - 1) {
        // not sure what the cart looked like before (e.g. an app wrote cart attributes meanwhile): the theme path handles gifts and
        // the final repaint, but the drawer can still be opened with a first server render right now (only the count is needed)
        if (pendingActive && drawerEl()) {
          var q = 0;
          (Array.isArray(j.items) ? j.items : [j]).forEach(function (a) { q += (a && a.quantity) || 0; });
          refreshDrawer({ items: [], item_count: Math.max(1, q), total_price: 0, nobl_fake: true }, { early: true });
        }
        return;
      }
      var predicted = predictAdd(knownCart.cart, j);
      if (!predicted) return;
      addStamps.push(Date.now());
      knownCart = { cart: predicted, seq: seq };
      afterMutation(predicted, { fromAdd: true });
    } catch (e) {}
  }

  // ---------------------------------------------------------------- gift rules (ported 1:1 from the old snippet)
  function qtyForKey(cart, key) {
    var k = S(key), total = 0, byKey = null;
    cart.items.forEach(function (i) { if (i.key === k) byKey = i.quantity; });
    if (byKey !== null) return byKey;
    cart.items.forEach(function (i) { if (S(i.variant_id || i.id) === k) total += i.quantity; });
    return total;
  }

  function analyze(cart, c) {
    var a = {
      excludedPrice: 0, lc: 0, gw: 0, gc: 0, gc25: 0, wk: 0, df: 0, at: 0,
      countLuggage: 0, fullSet: 0, cWk: 0, cWkOnly: 0, cDf: 0, cWrap: 0,
      wFirst: false, wOnly: false, dfFlag: false, cardProd: false, wrapProd: false, wrapProdCount: 0,
      airtagProd: false, subQty: 0, subIds: [], nonSub: false, mainIn: false
    };
    cart.items.forEach(function (item) {
      var vid = S(item.variant_id || item.id), pid = S(item.product_id), q = item.quantity || 0;
      if (pid === c.mainProductId) a.mainIn = true;
      var isSub = item.product_title === c.subName && c.exclSub.indexOf(pid) === -1;
      if (isSub) { a.subQty += q; a.subIds.push(vid); } else { a.nonSub = true; }

      if (c.excluded.indexOf(pid) > -1) a.excludedPrice += item.line_price;
      else if (vid === c.ids.luggage) a.lc += q;
      else if (vid === c.ids.wrap) a.gw += q;
      else if (vid === c.ids.card50) a.gc += q;
      else if (vid === c.ids.card25) a.gc25 += q;
      else if (vid === c.ids.weekender) a.wk += q;
      else if (vid === c.ids.duffel) a.df += q;
      else if (vid === c.ids.airtag) a.at += q;
      else {
        a.countLuggage += q;
        if (c.fullSet.indexOf(vid) > -1) a.fullSet += q;
      }
      if (c.wFirst.indexOf(vid) > -1) { a.cWk += q; a.cWrap += q; a.wFirst = true; }
      if (c.wOnly.indexOf(vid) > -1) { a.cWkOnly += q; a.wOnly = true; }
      if (c.duffel.indexOf(vid) > -1) { a.cDf += q; a.cWrap += q; a.dfFlag = true; }
      if (c.cardPids.indexOf(pid) > -1) a.cardProd = true;
      if (c.wrapPids.indexOf(pid) > -1) { a.wrapProd = true; a.wrapProdCount += q; }
      if (c.airtagPids.indexOf(pid) > -1) a.airtagProd = true;
    });
    return a;
  }

  function computeUpdates(cart) {
    var c = cfg();
    var updates = {};
    if (!c || !cart || !Array.isArray(cart.items)) return updates;

    // EU store: a single gift (Luggage Cover). Same rule as the old EU snippet: one cover per counted item once the
    // cart total (minus excluded products) reaches the threshold, none below it.
    if (c.mode === 'luggage') {
      var exV = c.exclVariants || [], exP = c.excluded || [], lcId = S(c.ids && c.ids.luggage), wrapId = S(c.wrapId);
      var exPrice = 0, cnt = 0, cur = 0;
      cart.items.forEach(function (it) {
        var vid = S(it.variant_id || it.id), pid = S(it.product_id), q = it.quantity || 0;
        var isEx = exV.indexOf(vid) > -1 || exP.indexOf(pid) > -1;
        if (lcId && vid === lcId) cur += q;
        if (isEx) exPrice += it.line_price;
        if (!(isEx || (lcId && vid === lcId) || (wrapId && vid === wrapId))) cnt += q;
      });
      var tgt = (cart.total_price - exPrice) >= c.threshold ? cnt : 0;
      if (lcId && cur !== tgt) updates[lcId] = tgt;
      return updates;
    }
    var a = analyze(cart, c);

    var expLuggage = a.countLuggage + a.fullSet;
    var expWrap = 0, expCard = 0, expWk = 0, expDf = 0;
    if (a.wFirst) { expCard = 1; expWrap = a.cWrap; expWk = a.cWk; }
    if (a.wOnly) { expWk += a.cWkOnly; }
    if (a.dfFlag) { expCard = 1; expWrap = a.cWrap; expDf = a.cDf; }
    var triple = a.cWkOnly * 1;
    if (a.wrapProd) { expWrap = expWrap + a.wrapProdCount + a.fullSet + triple; }
    if (a.cardProd) { expCard = 1; }

    var adjusted = cart.total_price - a.excludedPrice;
    var full = cart.total_price;
    var hasWeekender = a.wFirst || a.wOnly;
    var hasDuffel = a.dfFlag;
    var hasCardTarget = a.wFirst || a.cardProd || hasDuffel;
    var hasWrapTarget = a.wFirst || a.wrapProd || hasDuffel;
    var hasAirtagTarget = false;

    var gifts = [
      { id: c.ids.luggage, current: a.lc, expected: expLuggage, max: null, adj: true, target: false, flag: null },
      { id: c.ids.wrap, current: a.gw, expected: expWrap, max: null, adj: false, target: true, flag: hasWrapTarget },
      { id: c.ids.card50, current: a.gc, expected: expCard, max: 1, adj: false, target: true, flag: hasCardTarget },
      { id: c.ids.card25, current: a.gc25, expected: 0, max: 0, adj: false, target: false, flag: null, never: true },
      { id: c.ids.weekender, current: a.wk, expected: expWk, max: null, adj: false, target: true, flag: hasWeekender },
      { id: c.ids.duffel, current: a.df, expected: expDf, max: null, adj: false, target: true, flag: hasDuffel },
      { id: c.ids.airtag, current: a.at, expected: 0, max: 1, adj: false, target: true, flag: hasAirtagTarget }
    ];
    gifts.forEach(function (g) {
      if (!g.id) return;
      var shouldAdd;
      if (g.never) shouldAdd = false;
      else if (g.target && g.flag !== null) shouldAdd = !!g.flag;
      else if (g.target) shouldAdd = false;
      else if (g.adj) shouldAdd = adjusted >= c.threshold;
      else shouldAdd = full >= c.threshold;
      var targetQty = shouldAdd ? (g.max ? Math.min(g.expected, g.max) : g.expected) : 0;
      if (g.current !== targetQty) updates[g.id] = targetQty;
    });

    // NOBL Air subscription is always removed when it sits in the cart (checkout re-adds it).
    if (a.subQty > 0 && a.subIds.length) a.subIds.forEach(function (id) { updates[id] = 0; });

    // Free add-on must match the colour/quantity of its main product.
    if (c.addon && c.addon.main && c.addon.item) {
      var map = {};
      cart.items.forEach(function (i) {
        if (S(i.product_id) === c.addon.main) {
          var col = S((i.variant_options || [])[0]).toLowerCase().trim();
          if (col) map[col] = (map[col] || 0) + i.quantity;
        }
      });
      cart.items.forEach(function (i) {
        if (S(i.product_id) === c.addon.item) {
          var col = S((i.variant_options || [])[0]).toLowerCase().trim();
          if (!col) return;
          var want = map[col] || 0;
          if (i.quantity !== want) updates[i.key] = want;
        }
      });
    }
    return updates;
  }

  function effectiveUpdates(cart, updates) {
    var eff = {};
    Object.keys(updates).forEach(function (k) {
      if (qtyForKey(cart, k) !== (Number(updates[k]) || 0)) eff[k] = Number(updates[k]) || 0;
    });
    return eff;
  }

  function applyChanges(cart, changes) {
    var copy = { items: [], total_price: cart.total_price, item_count: cart.item_count };
    cart.items.forEach(function (it) {
      if (Object.prototype.hasOwnProperty.call(changes, it.key)) {
        var nq = Number(changes[it.key]) || 0;
        copy.total_price += (it.final_price || 0) * nq - (it.final_line_price || 0);
        copy.item_count += nq - it.quantity;
        if (nq > 0) {
          var n = Object.assign({}, it);
          n.quantity = nq;
          n.line_price = (it.price || 0) * nq;
          n.final_line_price = (it.final_price || 0) * nq;
          copy.items.push(n);
        }
      } else copy.items.push(it);
    });
    return copy;
  }

  // Product set that drives the upsell list (gift lines excluded: they come and go on their own).
  function giftVariantIds() {
    var c = cfg(), out = [];
    if (!c) return out;
    Object.keys(c.ids).forEach(function (k) { if (c.ids[k]) out.push(S(c.ids[k])); });
    return out;
  }
  function upsellSig(cart) {
    if (!cart || !cart.items) return '';
    var g = giftVariantIds(), seen = {}, list = [];
    cart.items.forEach(function (i) {
      if (g.indexOf(S(i.variant_id || i.id)) > -1) return;
      var p = S(i.product_id);
      if (!seen[p]) { seen[p] = 1; list.push(p); }
    });
    return list.sort().join(',');
  }

  // ---------------------------------------------------------------- gift sync (single flight)
  var syncPromise = null, syncAgain = false;

  function postUpdate(updates) {
    return window.fetch(ROOT + 'cart/update.js', {
      method: 'POST', headers: JSON_HEADERS, credentials: 'same-origin', body: JSON.stringify({ updates: updates })
    }).then(function (r) {
      if (!r.ok) throw new Error('update.js ' + r.status);
      return r.json();
    });
  }

  function runSync(hint) {
    if (syncPromise) { syncAgain = true; return syncPromise; }
    var hintValid = !!hint && pendingWrites === 0;
    syncPromise = (async function () {
      var wrote = false, last = null, loops = 0, lastSig = '', cart = hint || null, valid = hintValid;
      try {
        do {
          syncAgain = false;
          await idleWrites();
          if (!cart || !valid) cart = await getCart();
          valid = false;
          var eff = effectiveUpdates(cart, computeUpdates(cart));
          var keys = Object.keys(eff).sort();
          if (!keys.length) { last = cart; break; }
          var sig = JSON.stringify(keys.map(function (k) { return [k, eff[k]]; }));
          if (sig === lastSig || ++loops > 4) { last = cart; break; }
          lastSig = sig;
          var p = postUpdate(eff);
          var seqAt = writeSeq;
          var res = await p;
          wrote = true;
          last = res; cart = res;
          valid = (writeSeq === seqAt);   // nothing queued after our write: the response IS the cart
          syncAgain = true;               // verify the result once more
        } while (syncAgain);
      } catch (e) {
        if (window.console) console.warn('[NOBLCart] gift sync failed', e);
      } finally {
        syncPromise = null;
      }
      return { wrote: wrote, cart: last };
    })();
    return syncPromise;
  }

  // ---------------------------------------------------------------- quantity queue
  var pendingQty = {}; // key -> target
  var qtyTimer = null;
  var flushing = null;

  function hasPendingQty() { return !!qtyTimer || Object.keys(pendingQty).length > 0; }

  function loading(on) {
    try { document.documentElement.dispatchEvent(new CustomEvent(on ? 'theme:loading:start' : 'theme:loading:end', { bubbles: true })); } catch (e) {}
  }

  function setQuantity(key, qty) {
    key = S(key);
    if (!key) return false;
    var q = Math.max(0, parseInt(qty, 10) || 0);
    if (!qtyTimer && !flushing) loading(true);
    pendingQty[key] = q;
    clearTimeout(qtyTimer);
    qtyTimer = setTimeout(flushQuantities, 350);
    return true;
  }

  function currentTargetFor(key, fallback) {
    return Object.prototype.hasOwnProperty.call(pendingQty, key) ? pendingQty[key] : fallback;
  }

  async function flushQuantities() {
    clearTimeout(qtyTimer); qtyTimer = null;
    if (flushing) { await flushing; if (!Object.keys(pendingQty).length) return; }
    var keys = Object.keys(pendingQty);
    if (!keys.length) return;
    var changes = {};
    keys.forEach(function (k) { changes[k] = pendingQty[k]; });
    pendingQty = {};
    flushing = (async function () {
      var cart = null;
      try {
        var base = await getCart();
        var updates = Object.assign({}, changes);
        try {
          var predicted = applyChanges(base, changes);
          var gifts = effectiveUpdates(predicted, computeUpdates(predicted));
          Object.keys(gifts).forEach(function (k) { if (!Object.prototype.hasOwnProperty.call(updates, k)) updates[k] = gifts[k]; });
        } catch (e) {}
        cart = await postUpdate(updates);
        setKnownCart(cart);
      } catch (e) {
        if (window.console) console.warn('[NOBLCart] quantity update failed', e);
        cart = null;
      }
      return cart;
    })();
    var result = await flushing;
    flushing = null;
    if (hasPendingQty()) return; // a newer change is coming; it will repaint once
    await afterMutation(result, { fromQuantity: true });
    loading(false);
  }

  // ---------------------------------------------------------------- drawer repaint
  var refreshToken = 0, refreshing = null, refreshAgain = null;

  function drawerEl() { return document.querySelector('cart-drawer'); }
  function sectionId() {
    var d = drawerEl();
    if (!d) return null;
    if (d.nobl_sectionId) return d.nobl_sectionId;
    var s = d.closest('.shopify-section');
    return s ? s.id.replace(/^shopify-section-/, '') : null;
  }
  function safeToPaint() { return !hasPendingQty() && !flushing && pendingWrites === 0; }

  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  async function paintOnce(job) {
    var drawer = drawerEl(), sid = sectionId();
    if (!drawer || !sid) return;
    var token = ++refreshToken;
    var early = !!job.opts.early;   // painted while the gift write is still running (product first, gifts follow)
    var c = job.cart || await getCart();
    var count = c.item_count;
    var sig = c.nobl_fake ? lastUpsellSig : upsellSig(c);
    var hasUps = !!drawer.querySelector('.cart-drawer_upsells-container');
    var noUps = !!drawer.__noblNoUps;   // items are shown but the upsell list has not been loaded yet
    var needFull = job.opts.full || count === 0 || !hasUps || (!noUps && sig !== lastUpsellSig);
    // empty cart -> first item: paint everything except the (heavy) upsell list now, the upsell list follows right after
    var firstPaint = needFull && !job.opts.full && count > 0 && !hasUps;
    var url = ROOT + 'search?q=' + (firstPaint ? NOUPS_MARK : (needFull ? FULL_MARK : LITE_MARK)) + '&section_id=' + sid;
    var res = await origFetch(url, { credentials: 'same-origin', cache: 'no-store' });
    var html = await res.text();
    var tries = 0;
    while (!early && !safeToPaint() && tries < 200) {
      if (flushing) { try { await flushing; } catch (e) {} }
      else if (pendingWrites > 0) await idleWrites();
      else await sleep(50);
      tries++;
    }
    if ((!early && hasPendingQty()) || token !== refreshToken) return; // a newer change will repaint once it is done
    var isFull = needFull;   // the lite render also contains an (empty) upsell container, so only trust what we asked for
    var applied = await drawer.nobl_apply(html, isFull ? 'full' : 'lite', count);
    if (applied === false && !isFull) {
      var res2 = await origFetch(ROOT + 'search?q=' + FULL_MARK + '&section_id=' + sid, { credentials: 'same-origin', cache: 'no-store' });
      var html2 = await res2.text();
      if (hasPendingQty() || token !== refreshToken) return;
      await drawer.nobl_apply(html2, 'full', count);
      isFull = true;
    }
    if (firstPaint) { drawer.__noblNoUps = true; if (!early) loadUpsells(sid, sig, token); }
    else if (isFull) { drawer.__noblNoUps = false; lastUpsellSig = sig; }
    else if (noUps && !early) loadUpsells(sid, sig, token);
    if (pendingActive) pendingReveal();
  }

  // Upsell list for a drawer that was painted without it. If anything repainted the drawer in the meantime that repaint
  // brings its own list (lastUpsellSig stays unset, so the next repaint is a full one).
  function loadUpsells(sid, sig, token) {
    origFetch(ROOT + 'search?q=' + UPS_MARK + '&section_id=' + sid, { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var drawer = drawerEl();
        if (!drawer || token !== refreshToken) return;
        var src = new DOMParser().parseFromString(html, 'text/html').querySelector('.nobl-ups-root');
        var box = drawer.querySelector('.cart-drawer_upsells-container');
        if (!src || !box) return;
        box.replaceChildren(document.createRange().createContextualFragment(src.innerHTML));
        drawer.__noblNoUps = false;
        lastUpsellSig = sig;
      })
      .catch(function () {});
  }

  function refreshDrawer(cart, opts) {
    opts = opts || {};
    var drawer = drawerEl();
    if (!drawer || !sectionId() || typeof drawer.nobl_apply !== 'function') return Promise.resolve(false);
    if (refreshing) {
      var prev = refreshAgain;
      refreshAgain = { cart: cart || null, opts: Object.assign({}, prev && prev.opts, opts) };
      return refreshing;
    }
    refreshing = (async function () {
      try {
        var job = { cart: cart || null, opts: opts };
        while (job) {
          refreshAgain = null;
          try { await paintOnce(job); } catch (e) { if (window.console) console.warn('[NOBLCart] drawer refresh failed', e); }
          job = refreshAgain;
        }
      } finally {
        refreshing = null;
      }
      return true;
    })();
    return refreshing;
  }

  function broadcast(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    try {
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'nobl:sync', cart: cart, nobl: true } }));
    } catch (e) {}
  }

  // After any cart mutation: fix the gifts and repaint the drawer once.
  // When the gifts are about to change, the repaint waits for that write so the drawer never shows a gift-less state;
  // otherwise it repaints right away in parallel with the verification.
  async function afterMutation(cart, opts) {
    opts = opts || {};
    var resolveFirst;
    lastFirst = new Promise(function (r) { resolveFirst = r; });
    if (pendingActive) lastFirst.then(pendingEnd);
    try {
      var needsWrite = false;
      try { needsWrite = !!cart && Object.keys(effectiveUpdates(cart, computeUpdates(cart))).length > 0; } catch (e) {}
      var first = null;
      if (!needsWrite) { first = refreshDrawer(cart || null, opts); first.then(resolveFirst, resolveFirst); }
      else if (pendingActive && opts.fromAdd) { refreshDrawer(cart || null, { early: true }); }   // product first, the gifts follow
      var res = await runSync(cart || undefined);
      if (first) await first;
      if (res && (res.wrote || needsWrite)) {
        await refreshDrawer(res.cart, {});
      } else if (opts.fromAdd) {
        // the repaint ran on a predicted cart: confirm against the server (no extra drawer render unless gifts change)
        lastCart = null;
        var res2 = await runSync();
        if (res2 && res2.wrote) { await refreshDrawer(res2.cart, {}); }
        if (res2 && res2.cart) res = res2;
      }
      if (res && res.cart) broadcast(res.cart);
      else if (cart) broadcast(cart);
    } finally {
      resolveFirst();
    }
  }

  // ---------------------------------------------------------------- settle (used before checkout)
  async function settled() {
    var guard = 0, res;
    do {
      guard++;
      if (hasPendingQty()) await flushQuantities();
      if (flushing) await flushing;
      await idleWrites();
      if (syncPromise) await syncPromise;
      if (lastCart && Date.now() - lastCart.at > 30000) cart_reset();
      res = await runSync();
    } while ((res.wrote || hasPendingQty() || pendingWrites > 0) && guard < 25);
    return res && res.cart;   // the drawer repaint is not needed to go to checkout
  }
  function cart_reset() { lastCart = null; }

  // ---------------------------------------------------------------- events from the theme
  function onCartChange(detail, drawer) {
    if (!detail || detail.nobl) return Promise.resolve();
    var cart = detail.cart && Array.isArray(detail.cart.items) ? detail.cart : null;
    var now = Date.now();
    while (addStamps.length && now - addStamps[0] > 20000) addStamps.shift();
    if (detail.baseEvent === 'variant:add' && addStamps.length) {
      addStamps.shift();           // this add was already handled from its response
      return lastFirst || Promise.resolve();
    }
    if (cart) setKnownCart(cart);
    afterMutation(cart, {});
    return lastFirst || Promise.resolve();
  }
  function onCartRefresh() { afterMutation(null, {}); }

  // ---------------------------------------------------------------- checkout guard
  var checkoutBusy = false;
  function setBusy(el, on) {
    if (!el) return;
    if (on) { el.setAttribute('aria-busy', 'true'); el.style.opacity = '0.6'; el.style.pointerEvents = 'none'; }
    else { el.removeAttribute('aria-busy'); el.style.opacity = ''; el.style.pointerEvents = ''; }
  }
  async function guardCheckout(trigger, go) {
    if (checkoutBusy) return;
    checkoutBusy = true;
    setBusy(trigger, true);
    try { await settled(); } catch (e) {}
    checkoutBusy = false;
    setBusy(trigger, false);
    go();
  }
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('form[action$="/checkout"]')) return;
    if (form.__noblPass || !CFG) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var btn = form.querySelector('[type="submit"]');
    guardCheckout(btn, function () {
      // the drawer may have been repainted while waiting: submit the form that is on the page now
      var target = form.isConnected ? form : document.querySelector('form[action$="/checkout"]');
      if (!target) { if (!(window.NOBLCart && window.NOBLCart.noNavigate)) window.location.assign(form.getAttribute('action') || '/checkout'); return; }
      target.__noblPass = true;
      if (window.NOBLCart && window.NOBLCart.noNavigate) return;
      HTMLFormElement.prototype.submit.call(target);
    });
  }, true);
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest && e.target.closest('a[href^="/checkout"], a[href*="/checkout?"]');
    if (!a || a.__noblPass || !CFG) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    guardCheckout(a, function () {
      if (window.NOBLCart && window.NOBLCart.noNavigate) return;
      window.location.assign(a.href);
    });
  }, true);

  // ---------------------------------------------------------------- init
  window.NOBLCart = {
    ready: false,
    noNavigate: false,
    setQuantity: setQuantity,
    currentTargetFor: currentTargetFor,
    onCartChange: onCartChange,
    onCartRefresh: onCartRefresh,
    refreshDrawer: refreshDrawer,
    settled: settled,
    runSync: runSync,
    computeUpdates: computeUpdates,
    getCart: getCart,
    cartKeyForLine: function (line) { var c = lastCart && lastCart.cart; return c && c.items[line - 1] ? c.items[line - 1].key : null; },
    _debug: function () { return { knownSeq: knownCart ? knownCart.seq : null, writeSeq: writeSeq, pendingWrites: pendingWrites, pendingQty: pendingQty, syncing: !!syncPromise, flushing: !!flushing, refreshing: !!refreshing, upsellSig: lastUpsellSig }; }
  };

  function init() {
    var c = cfg();
    if (!c) return;
    if (c.cart && Array.isArray(c.cart.items)) { lastCart = { cart: c.cart, seq: writeSeq, at: Date.now() }; knownCart = { cart: c.cart, seq: writeSeq }; lastUpsellSig = upsellSig(c.cart); }
    window.NOBLCart.ready = true;
    runSync(c.cart || undefined).then(function (res) {
      if (res && res.wrote) { refreshDrawer(res.cart, {}); broadcast(res.cart); }
    });
  }
  window.addEventListener('pageshow', function (e) { if (e.persisted) { lastCart = null; afterMutation(null, { full: true }); } });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
